import { motion } from "framer-motion";
import { SectionEyebrow, SectionTitle, FadeUp } from "./_shared";
import { MILESTONES } from "@/data/cpi";

const WATCH_IMAGE =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/76nndxbn_futuristic_close-up_of_a_smartwatch_on_a_wrist_displaying_he.webp";

function MilestoneCard({ m, index, last }) {
  return (
    <div
      data-testid={`milestone-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`relative border-2 bg-background p-6 md:p-7 h-full ${
        last ? "border-accent" : "border-primary/15"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] font-bold text-primary/60">
          {m.label}
        </span>
        {last && (
          <span className="font-mono text-[0.55rem] uppercase tracking-[0.18em] font-bold text-accent">
            Current
          </span>
        )}
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <span className={`font-display text-4xl md:text-5xl font-bold ${last ? "text-accent" : "text-primary"}`}>
          {m.cpi}
        </span>
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-foreground/50">
          CPI
        </span>
      </div>
      <div className="mt-2 h-1.5 bg-primary/10 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${m.cpi}%` }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.9, delay: 0.1 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
          className={`h-full ${last ? "bg-accent" : "bg-primary"}`}
        />
      </div>

      <dl className="mt-5 space-y-2">
        {[
          ["Body fat", m.bodyFat],
          ["Avg sleep", m.sleep],
          ["Energy", m.energy],
        ].map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between border-b border-border pb-1.5 last:border-0">
            <dt className="text-xs text-foreground/55">{k}</dt>
            <dd className="font-mono text-xs font-bold text-primary">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function LongitudinalSection() {
  return (
    <section
      id="longitudinal"
      data-testid="longitudinal-section"
      className="py-24 md:py-32 bg-background border-t border-border"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <FadeUp className="lg:col-span-5">
            <SectionEyebrow>Longitudinal Intelligence</SectionEyebrow>
            <SectionTitle className="mt-4 text-[clamp(1.9rem,4.5vw,3.25rem)]">
              Your Biology Changes. <em className="not-italic text-accent">Your Plan Should Too.</em>
            </SectionTitle>
            <p className="mt-5 text-lg text-foreground/75 leading-relaxed">
              New data updates your CPI, priorities and plan.
            </p>
            <div className="mt-8 aspect-[4/3] overflow-hidden border-2 border-primary/20 bg-card">
              <img
                src={WATCH_IMAGE}
                alt="Wearable heart-rate and HRV data supporting longitudinal health tracking"
                data-testid="longitudinal-wearable-image"
                className="h-full w-full object-cover object-center"
                loading="lazy"
              />
            </div>
          </FadeUp>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {MILESTONES.map((m, i) => (
              <FadeUp key={m.label} delay={i * 0.08}>
                <MilestoneCard m={m} index={i} last={i === MILESTONES.length - 1} />
              </FadeUp>
            ))}
          </div>
        </div>

        <FadeUp delay={0.2} className="mt-8 text-center">
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-foreground/45">
            Illustrative sample data · Fasting insulin 14.2 → 6.8 µIU/mL over the same period
          </p>
        </FadeUp>
      </div>
    </section>
  );
}
